/**
 * Reinforcement cards, adapted from `@mechrulez/data` CARDS / CARDS_DATA (gamedoc/50_reinforcement_cards.md
 * §3, §5, §7, §9).
 *
 * `CARDS` is the standard-PvP draft roster: every catalogued power / equipment / pool-specialist card
 * with a known rarity (the 80 unit-variant cards are not offered in this version). Level, price,
 * cooldown and names come from the data records; the data descriptions are localisation templates
 * with `{n}` placeholders, so `CARD_TEXT_RU` carries the resolved text the client renders (numbers from
 * the records' `numbersSeen`). Only the economy cards carry an `effect` this version applies — their
 * magnitudes are `CARD_EFFECTS` where the data marks the value UNKNOWN; every other card is offered
 * but shown as «недоступно в этой версии».
 */
import { CARDS as DATA_CARDS, CARDS_DATA, ECONOMY, encodeUnitDropId, skipReward as dataSkipReward } from '@mechrulez/data';
/** Default price per level (`ReinforceItemPrice`, index = level). */
export const CARD_PRICE_BY_LEVEL = [0, ...ECONOMY.cards.priceByLevelDefault];
/** Text shown on cards this version does not apply. */
export const CARD_UNAVAILABLE_RU = 'недоступно в этой версии';
/** Resolved RU card texts (the data templates carry `{n}` placeholders; numbers per `numbersSeen` / 50 §9). */
const CARD_TEXT_RU = {
    10007: 'Прочность Генератора щита увеличена.',
    10008: 'Урон Сторожевой ракеты увеличен на 200%.',
    10009: 'Время телепортации на фланги сокращено.',
    1305003: 'Отряд получает меньше урона в начале боя и невосприимчив к ЭМИ, поджогу, кислоте и вырождению.',
    1308001: 'Невосприимчивость к ЭМИ, взлому и параличу ключевого здания.',
    13030005: 'Скорость +5, АТК +35%.',
    13040001: 'Отряд можно перемещать в каждой фазе развертывания.',
    1500002: 'Задаёт путь движения выбранному отряду.',
    200001: '20 000 урона щитам, отключает технологии и снижает скорость на 40% на 25 с.',
    300001: 'Управляемая ракета наносит 3 000 урона.',
    800001: 'Сбрасывает энергетический щит с 50 000 ОЗ.',
    1000001: 'Дополнительное перемещение выбранного отряда в этой фазе развертывания.',
    1100001: 'Опыт выбранного отряда заполняется до максимума.',
    1200001: 'В отмеченной точке продолжают появляться Ползуны.',
    1200002: 'Десантирует Носорога (1).',
    1200003: 'Призывает рой Ос.',
    13030001: 'Радиус атаки +20 м (только для юнитов дальнего боя).',
    13030002: 'ОЗ +75%.',
    13030003: 'АТК увеличена.',
    10004: 'Количество юнитов, которых можно развернуть за раунд, увеличено на 1.',
    20003: 'Снижает стоимость установки технологий для всех юнитов на 50.',
    20004: 'Скорость передвижения всех юнитов увеличена.',
    20007: 'Получайте 50 ед. дополнительных припасов каждый раунд.',
    20022: 'Стоимость найма гигантов снижена на 100.',
    100002: 'Стена огня, 270 урона в секунду.',
    200003: 'Союзники в области получают на 30% меньше урона в течение 20 с.',
    300003: 'Залп ракет по области.',
    500002: 'Полоса кислоты: −3% ОЗ в секунду и +250% получаемого урона, держится 1 раунд.',
    600002: 'Полоса дыма, снижающая радиус атаки внутри.',
    1307001: '[Наземный гигант] барьер, защищающий юнитов рядом.',
    1309001: 'ОЗ увеличены, часть нанесённого урона возвращается как ОЗ.',
    13010001: 'Щит, поглощающий урон, равный ОЗ юнита.',
    13020001: 'Восстанавливает процент максимальных ОЗ в секунду.',
    13030007: 'АТК +50%, ОЗ +50%.',
    13030010: 'АТК и ОЗ увеличены, +50 припасов за раунд; гибель носителя уничтожает всю армию.',
    10003: 'Начиная со следующего раунда, получайте 100 ед. дополнительных припасов каждый раунд.',
    20001: 'ОЗ всех юнитов увеличены.',
    20002: 'АТК всех юнитов увеличена.',
    20006: 'Радиус атаки всех юнитов дальнего боя увеличен.',
    20023: 'Стоимость найма всех юнитов, кроме гигантов, снижена на 50.',
    200002: 'Электромагнитный импульс по большой области.',
    300004: 'Через 15 с наносит 70 000 урона.',
    300005: '2 000 урона и −65% скорости на 20 с.',
    300006: 'Медленный луч с непрерывным уроном.',
    300007: '70 000 урона в радиусе 30 м; не останавливается энергетическими щитами.',
    1200004: 'Призывает Владыку (1).',
    1200005: 'Десантирует Вулкан.',
    1306001: '[Гигант] производит Кувалды в бою.',
    1306002: '[Гигант] производит Мустангов в бою.',
    1306003: '[Гигант] производит Стальные шары в бою.',
    13030004: 'АТК +25%, ОЗ +25%, стоимость повышения снижена на 100.',
    13030006: 'ОЗ +150%.',
};
/** Magnitudes of the economy cards this version applies (data marks some values UNKNOWN; 50 §9 / 40 §1.3). */
const CARD_EFFECTS = {
    10004: { buySlots: 1 },
    20003: { techDiscount: -50 },
    20007: { roundSupply: 50 },
    20022: { recruitDiscountGiant: -100 },
    10003: { roundSupply: 100, fromNextRound: true },
    20023: { recruitDiscountLight: -50 },
};
/** Fills `{n}` placeholders from a list of values; leaves unknown ones as they are. */
function fillTemplate(text, values) {
    return text.replace(/\{(\d+)\}/g, (m, i) => values[Number(i)] ?? m);
}
function familyOf(r) {
    return r.category === 'power' ? 'power' : r.category === 'equipment' ? 'equipment' : 'specialist';
}
export function cardFromRecord(r) {
    if (r.rarityLevel === null || r.category === 'unitVariant' || r.category === 'unitDrop')
        return null;
    const level = Math.max(1, Math.min(4, r.rarityLevel));
    const price = r.cost ?? CARD_PRICE_BY_LEVEL[level] ?? 0;
    const def = {
        id: r.id,
        family: familyOf(r),
        level,
        price,
        nameRu: r.nameRu,
        nameEn: r.nameEn,
        descriptionRu: CARD_TEXT_RU[r.id] ?? fillTemplate(r.descriptionRu, r.numbersSeen ? [r.numbersSeen] : []),
        record: r,
    };
    if (r.category === 'power' && r.cooldownRounds !== null)
        def.cooldown = r.cooldownRounds;
    const effect = r.implementable === 'now' ? CARD_EFFECTS[r.id] : undefined;
    if (effect)
        def.effect = effect;
    return def;
}
/** The draft roster, in ascending id order. */
export const CARDS = DATA_CARDS.map(cardFromRecord)
    .filter((c) => c !== null)
    .sort((a, b) => a.id - b.id);
export const CARDS_BY_LEVEL = {
    1: CARDS.filter((c) => c.level === 1),
    2: CARDS.filter((c) => c.level === 2),
    3: CARDS.filter((c) => c.level === 3),
    4: CARDS.filter((c) => c.level === 4),
};
export function cardById(id) {
    return CARDS.find((c) => c.id === id);
}
/** Per-round level weights (percent) of the standard draw: `ReinforceItemProbability`. */
export function cardLevelWeights(round) {
    if (round <= 2)
        return [60, 40, 0, 0];
    if (round === 3)
        return [30, 40, 30, 0];
    return [25, 25, 25, 25];
}
/** Scene-1 offer matrix: the unit-drop pool per round → (count × rank) buckets. */
export const UNIT_DROPS = (() => {
    const out = {};
    for (const row of CARDS_DATA.unitDrops.pool)
        out[row.round] = row.cells.map((c) => ({ count: c.count, rank: c.rank, units: [...c.unitIds] }));
    return out;
})();
/** Rounds the pool covers, ascending. */
export const UNIT_DROP_ROUNDS = Object.keys(UNIT_DROPS)
    .map(Number)
    .sort((a, b) => a - b);
/** Units the shop never sells («Rare Unit can only be unlocked via random Reinforcement cards»). */
export const RARE_DROP_UNITS = [31, 28, 25, 30, 27, 26, 29];
const OBSERVED_DROP_PRICES = new Map(CARDS_DATA.unitDrops.observedPrices.map((p) => [p.id, p.price]));
/**
 * Unit-drop price: the observed row price when the corpus saw that exact card, else the inferred
 * pattern — rare units 50, giants 100, otherwise 0 / 50 / 100 / 200 by count × rank (50 §9.6).
 */
export function unitDropPrice(typeId, unitCost, count, rank, round) {
    if (round !== undefined) {
        const seen = OBSERVED_DROP_PRICES.get(encodeUnitDropId({ scene: 1, round, count, rank, unitId: typeId }));
        if (seen !== undefined)
            return seen;
    }
    if (RARE_DROP_UNITS.includes(typeId))
        return 50;
    if (unitCost >= 400)
        return 100;
    const value = count * rank;
    return value <= 1 ? 0 : value === 2 ? 50 : value === 3 ? 100 : 200;
}
/** «ПРОПУСТИТЬ» reward: +50 flat, +50 × (round − 1) on a unit-drop round. */
export function skipReward(round, isUnitRound) {
    return dataSkipReward(Math.max(2, round), isUnitRound);
}
//# sourceMappingURL=cards.js.map